import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { config } from '@/lib/config';
import { tokenStorage } from './storage';
import { useAuthStore } from './store';

/**
 * Per-request opt-out from the global 401 → "log out and bounce to /login"
 * behavior. Set this on a request config when a sub-resource (image fetch,
 * polling status, etc.) might 401 for benign reasons (expired token on a
 * specific row, missing role) and you don't want one bad fetch to nuke the
 * whole session.
 */
type SkipUnauthorizedConfig = InternalAxiosRequestConfig & { _skipUnauthorizedRedirect?: boolean };

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${config.API_URL}/api`,
  timeout: 30_000,
});

apiClient.interceptors.request.use((requestConfig) => {
  const token = tokenStorage.read();
  if (token && !requestConfig.headers.has('Authorization')) {
    requestConfig.headers = requestConfig.headers ?? {};
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

let onUnauthorized: () => void = () => {
  tokenStorage.clear();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// 403 from a sub-resource fetch (e.g. one of many /reporting endpoints) should
// not yank the user out of the page they're on. Default = no-op; callers can
// install a handler if they want to navigate. Components surface 403s in-place
// via TanStack Query's `isError` + `error.response.status` instead.
let onForbidden: () => void = () => {};

export const setUnauthorizedHandler = (handler: () => void) => {
  onUnauthorized = handler;
};

export const setForbiddenHandler = (handler: () => void) => {
  onForbidden = handler;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url ?? '';

    // Don't redirect on 401 from the auth endpoints themselves —
    // a 401 on /oauth/authorize is the MFA challenge or bad credentials,
    // both of which the login page is responsible for handling.
    const isAuthEndpoint = url.includes('/oauth/authorize') || url.includes('/oauth/logout');
    const skipRedirect = (error.config as SkipUnauthorizedConfig | undefined)?._skipUnauthorizedRedirect === true;

    if (status === 401 && !isAuthEndpoint && !skipRedirect) {
      // Sync clear immediately, then dispatch the redirect (OWASP A07 — no delay).
      // A response from an ended session must not log out its replacement.
      const token = useAuthStore.getState().token;
      if (token && error.config?.headers.get('Authorization') === `Bearer ${token}`) {
        useAuthStore.getState().setToken(null);
        onUnauthorized();
      }
    } else if (status === 403 && !isAuthEndpoint) {
      // OWASP A01 — 403 means authenticated but not authorized for this resource.
      // Keep the token. Don't auto-navigate: a 403 from one sub-resource on a
      // page (e.g. a flag-gated reporting endpoint) shouldn't unmount the rest
      // of the page. Components can surface a deny state inline. The
      // `/forbidden` route stays addressable; an explicit handler can be
      // installed via `setForbiddenHandler` if a top-level page wants to
      // navigate on its own primary fetch failing 403.
      onForbidden();
    }
    return Promise.reject(error);
  },
);
